import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../contexts/NotificationContext';
import i18n from '../../i18n';
import './NotificationBell.css';

/**
 * Header bell showing unread notification count with a dropdown of recent
 * notifications. Marks all read on open; supports per-item dismiss + clear all.
 * @returns {JSX.Element} The notification bell with its dropdown.
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const { notifications, unreadCount, markAllRead, dismissNotification, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToggle = () => {
    setOpen(prev => !prev);
    if (!open) markAllRead();
  };

  const activeNotifications = notifications.filter(n => !n.dismissed);

  return (
    <div className="notification-bell" ref={ref}>
      <button className="bell-btn" onClick={handleToggle}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="bell-dropdown">
          <div className="bell-dropdown-header">
            <span className="bell-dropdown-title">{t('settings.notifTitle')}</span>
            {activeNotifications.length > 0 && (
              <button className="bell-clear-btn" onClick={clearAll}>{t('notify.clearAll')}</button>
            )}
          </div>
          <div className="bell-dropdown-list">
            {activeNotifications.length === 0 ? (
              <div className="bell-empty">{t('notify.empty')}</div>
            ) : (
              activeNotifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  className={`bell-item ${!n.read ? 'unread' : ''}`}
                  onClick={() => {
                    if (n.action) n.action();
                    dismissNotification(n.id);
                    setOpen(false);
                  }}
                >
                  <span className="bell-item-message">{n.message}</span>
                  <span className="bell-item-time">{formatTime(n.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatTime(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return i18n.t('notify.justNow');
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
